import { createFileRoute } from "@tanstack/react-router";
import { CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CardLink } from "@/components/primitives/card-link";
import { PageContainer } from "@/components/primitives/layout";
import {
  ArticleBody,
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHeading,
  ArticleSection,
  ArticleSectionHeader,
} from "@/components/primitives/article";

export const Route = createFileRoute("/learn/")({
  head: () => ({
    meta: [{ title: "Learn - Scoopulator" }],
  }),
  component: LearnPage,
});

type ArticleHref =
  | "/learn/why-use-an-ice-cream-calculator"
  | "/learn/which-ingredients-do-i-need-to-make-great-ice-cream"
  | "/learn/how-to-fix-hard-ice-cream"
  | "/learn/fpdf"
  | "/learn/faq";

function ArticleCard({
  title,
  description,
  href,
  imageSrc,
}: {
  title: string;
  description: string;
  href: ArticleHref;
  imageSrc?: string;
}) {
  return (
    <CardLink className="overflow-hidden" href={href} srLabel={title}>
      {imageSrc ? (
        <div className="bg-muted relative aspect-video w-full overflow-hidden">
          <img
            src={imageSrc}
            alt={title}
            width={808}
            height={360}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      ) : null}
      <CardHeader className="h-full">
        <div>
          <CardTitle className="text-lg group-hover:underline">
            {title}
          </CardTitle>
          <CardDescription className="text-md">{description}</CardDescription>
        </div>
      </CardHeader>
    </CardLink>
  );
}

function LearnPage() {
  return (
    <PageContainer>
      <ArticleContainer>
        <ArticleHeader>
          <ArticleHeading>Learn</ArticleHeading>
          <ArticleDescription>
            Learn more about the science behind making balanced ice cream
            recipes.
          </ArticleDescription>
        </ArticleHeader>
        <ArticleBody>
          <ArticleSection>
            <ArticleSectionHeader id="beginner">Beginner</ArticleSectionHeader>
            <ArticlesContainer>
              <ArticleCard
                title="Why use an ice cream calculator?"
                description="Learn how an ice cream calculator can help you make better ice cream."
                href="/learn/why-use-an-ice-cream-calculator"
                imageSrc="/articles/why.webp"
              />
              <ArticleCard
                title="Which ingredients do I need to make great ice cream?"
                description="A beginner's guide to the essential ingredients for making ice cream at home."
                href="/learn/which-ingredients-do-i-need-to-make-great-ice-cream"
                imageSrc="/articles/ingredients.webp"
              />
              <ArticleCard
                title="How to fix hard ice cream"
                description="Learn how to adjust your ice cream recipe to make it softer and easier to scoop."
                href="/learn/how-to-fix-hard-ice-cream"
                imageSrc="/articles/too-hard.webp"
              />
            </ArticlesContainer>
          </ArticleSection>
          <ArticleSection>
            <ArticleSectionHeader id="intermediate">
              Intermediate
            </ArticleSectionHeader>
            <ArticlesContainer>
              <ArticleCard
                title="How to calculate FPDF / PAC"
                description="Learn how to calculate FPDF to make soft, scoopable ice cream."
                href="/learn/fpdf"
              />
            </ArticlesContainer>
          </ArticleSection>
          <ArticleSection>
            <ArticleSectionHeader id="reference">
              Reference
            </ArticleSectionHeader>
            <ArticlesContainer>
              <ArticleCard
                title="Frequently asked questions"
                description="Answers to common questions about Scoopulator and ice cream science."
                href="/learn/faq"
              />
            </ArticlesContainer>
          </ArticleSection>
        </ArticleBody>
      </ArticleContainer>
    </PageContainer>
  );
}

function ArticlesContainer({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}
